/**
 * The clinic-wide DeliveredSession query (ZOY-262, verified):
 * GET /v1/delivered-sessions?query=<JSON> on the ZOY-285 numbered envelope,
 * plus a `summary` over the FULL filtered set. Allow-list: deliveredAt,
 * paidAt, createdAt, payableAmount, patientId, bookingId (`in` ≤ 100),
 * serviceId, packageItemId, deliveredByUid, recordedBy, paymentStatus,
 * paymentMethod, locationType, effective (server default: effective only —
 * history asks for both explicitly).
 */

import {
  buildQueryParam,
  type PagedResult,
  type QueryFilter,
  type QuerySort,
} from '@/core/api/querySpec';
import { apiRequest } from '@/core/api/http';
import type {
  DeliveredSessionListItem,
  DeliveredSessionSummary,
  PaymentMethod,
  PaymentStatus,
} from '@/core/clinic/sessionsApi';

export interface SessionQueryFilters {
  patientId?: string;
  deliveredByUid?: string;
  serviceId?: string;
  packageItemId?: string;
  bookingId?: string;
  bookingIds?: readonly string[];
  from?: Date;
  to?: Date;
  /** Omitted = the server default (effective only); `'any'` asks for both. */
  effective?: boolean | 'any';
  /** One status, or several (`in`) — e.g. OWING_STATUSES for "money still owed". */
  paymentStatus?: PaymentStatus | readonly PaymentStatus[];
  paymentMethod?: PaymentMethod;
}

/** The filters in the verified DSL shape. */
export function sessionFilters(f: SessionQueryFilters): QueryFilter[] {
  const out: QueryFilter[] = [];
  if (f.patientId) out.push({ field: 'patientId', op: 'eq', value: f.patientId });
  if (f.deliveredByUid) out.push({ field: 'deliveredByUid', op: 'eq', value: f.deliveredByUid });
  if (f.serviceId) out.push({ field: 'serviceId', op: 'eq', value: f.serviceId });
  if (f.packageItemId) out.push({ field: 'packageItemId', op: 'eq', value: f.packageItemId });
  if (f.bookingId) out.push({ field: 'bookingId', op: 'eq', value: f.bookingId });
  if (f.bookingIds && f.bookingIds.length > 0)
    out.push({ field: 'bookingId', op: 'in', value: f.bookingIds.slice(0, 100) });
  if (f.from && f.to)
    out.push({
      field: 'deliveredAt',
      op: 'between',
      value: [f.from.toISOString(), f.to.toISOString()],
    });
  else if (f.from) out.push({ field: 'deliveredAt', op: 'gte', value: f.from.toISOString() });
  else if (f.to) out.push({ field: 'deliveredAt', op: 'lte', value: f.to.toISOString() });
  if (f.effective === 'any') out.push({ field: 'effective', op: 'in', value: [true, false] });
  else if (f.effective !== undefined)
    out.push({ field: 'effective', op: 'eq', value: f.effective });
  if (typeof f.paymentStatus === 'string')
    out.push({ field: 'paymentStatus', op: 'eq', value: f.paymentStatus });
  else if (f.paymentStatus && f.paymentStatus.length > 0)
    out.push({ field: 'paymentStatus', op: 'in', value: [...f.paymentStatus] });
  if (f.paymentMethod) out.push({ field: 'paymentMethod', op: 'eq', value: f.paymentMethod });
  return out;
}

export interface SessionPageSpec {
  filters: QueryFilter[];
  sort: QuerySort[];
  limit: number;
  page?: number;
  cursor?: string;
}

export interface DeliveredSessionsPage extends PagedResult<DeliveredSessionListItem> {
  summary: DeliveredSessionSummary;
}

/** THE clinic-wide query. */
export const listDeliveredSessions = (spec: SessionPageSpec) =>
  apiRequest<DeliveredSessionsPage>({
    base: 'clinic',
    path: '/v1/delivered-sessions',
    query: { query: buildQueryParam(spec) },
  });
